import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { filesAPIService } from '~/api/files';
import QRCodeCornerStyle from '~/components/QRCodeDesign/QRCodeCornerStyle/QRCodeCornerStyle';
import QRCodeCorrectionLevel from '~/components/QRCodeDesign/QRCodeCorrectionLevel';
import QRCodeFrame from '~/components/QRCodeDesign/QRCodeFrame';
import QRCodeLogo from '~/components/QRCodeDesign/QRCodeLogo/QRCodeLogo';
import QRCodePattern from '~/components/QRCodeDesign/QRCodePattern/QRCodePattern';
import { Text } from '~/components/ui/text';
import { BASIC_GRADIENT_STYLES } from '~/constants/common';
import { QrCodeFrameItem } from '~/types/qrCode';
import { getFileFromLocalSource } from '~/utils/common';

const DEFAULT_COLOR = '#000000';

export const QrcodeStylesEditor = ({
  qrCode,
  qrCodeStylesTemplates,
  onRemoveImage,
  onAddImage,
  onUpdateStyles,
  containerStyle,
}) => {
  const { t } = useTranslation();

  const [selectedLogo, setSelectedLogo] = useState({ name: 'empty', file: null });

  const qrCodeDataStylesRef = useRef(qrCode?.style);

  const onRemoveImageHandler = () => {
    setSelectedLogo({ name: 'empty', file: null });

    onRemoveImage({ embedded_logo_id: null });
  };

  const onAddImageHandler = async (item) => {
    if (item.name === 'empty') {
      onRemoveImageHandler();
    } else {
      setSelectedLogo(item);
      const fileURL = await getFileFromLocalSource(window.URL.createObjectURL(item.file));
      const response = await filesAPIService.uploadImage(fileURL);

      onAddImage({ embedded_logo_id: response.data });
    }
  };

  const updateQrCodeAppear = (field, id) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      [field]: {
        ...qrCodeDataStylesRef.current[field],
        style: id,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    onUpdateStyles(newStyle);
  };

  const updateQrCodeCorrection = (id) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      errorCorrectionLevel: id,
    };

    qrCodeDataStylesRef.current = newStyle;

    onUpdateStyles(newStyle);
  };

  const handleChangeFrame = (newFrame: QrCodeFrameItem) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      frame: {
        ...qrCodeDataStylesRef.current.frame,
        style: newFrame.id,
        phrase_color: newFrame.defaultTextColor,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    onUpdateStyles(newStyle);
  };

  const handleChangeColor = (newColor) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        shape: {
          ...qrCodeDataStylesRef.current?.shape,
          color: newColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeGradient = ({ field, value, parentField, parentDbField }) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        [parentDbField]: {
          ...qrCodeDataStylesRef.current[parentField],
          color: {
            ...qrCodeDataStylesRef.current[parentField].color,
            [field]: value,
          },
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeIsGradient = ({ field, checked, defaultValue, dbField }) => {
    if (qrCode?.style) {
      if (checked) {
        const newStyle = {
          ...qrCodeDataStylesRef.current,
          [dbField]: {
            ...qrCodeDataStylesRef.current[field],
            color: {
              ...BASIC_GRADIENT_STYLES,
              color: defaultValue ?? DEFAULT_COLOR,
            },
          },
        };

        qrCodeDataStylesRef.current = newStyle;

        onUpdateStyles(newStyle);
      } else {
        const newStyle = {
          ...qrCodeDataStylesRef.current,
          [dbField]: {
            ...qrCodeDataStylesRef.current[field],
            color: DEFAULT_COLOR,
          },
        };

        qrCodeDataStylesRef.current = newStyle;

        onUpdateStyles(newStyle);
      }
    }
  };

  const handleChangeBorderStyle = (newBorderStyle) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          style: newBorderStyle,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeCenterStyle = (newCenterStyle) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          style: newCenterStyle,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeBorderColor = (newBorderColor) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          color: newBorderColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeCenterColor = (newCenterColor) => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          color: newCenterColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleSwitchColors = () => {
    if (qrCode?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          color: qrCodeDataStylesRef.current?.cornerBorder?.color,
        },
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          color: qrCodeDataStylesRef.current?.cornerCenter?.color,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  useEffect(() => {
    (async function () {
      if (qrCode?.embeddedLogo) {
        const imgResponse = await fetch(qrCode.embeddedLogo);
        const blob = await imgResponse.blob();
        const logoFile = new File([blob], qrCode.embeddedLogo, blob);

        setSelectedLogo({ name: 'empty', file: logoFile, fileURL: qrCode.embeddedLogo });
      }
    })();
  }, []);

  useEffect(() => {
    if (qrCode?.style) {
      qrCodeDataStylesRef.current = qrCode.style;
    }
  }, [qrCode]);

  return (
    <Box className="templates" sx={containerStyle}>
      <Text variant="h2" sx={{ marginBottom: '24px' }}>
        {t('thirdStep:designQr')}
      </Text>

      <QRCodeFrame
        frames={qrCodeStylesTemplates?.frames}
        defaultSelectedFrame={qrCode?.style?.frame?.style}
        onSelectFrame={handleChangeFrame}
        qrStyles={qrCode?.style}
        onUpdateQrStyles={onUpdateStyles}
      />
      <QRCodePattern
        defaultSelectedShape={qrCode?.style?.shape?.style}
        onSelectShape={(newShape) => updateQrCodeAppear('shape', newShape)}
        isAdvanced
        onChangeColor={handleChangeColor}
        onChangeGradient={(params) => handleChangeGradient({ ...params, parentField: 'shape', parentDbField: 'shape' })}
        onChangeIsGradient={(e) =>
          handleChangeIsGradient({
            field: 'shape',
            dbField: 'shape',
            checked: e.target.checked,
            defaultValue: qrCode?.style?.shape?.color,
          })
        }
        qrCodeStyles={qrCode?.style}
      />
      <QRCodeCornerStyle
        onChangeBorderStyle={handleChangeBorderStyle}
        onChangeCenterStyle={handleChangeCenterStyle}
        onSwitchBorderAndCenterColors={handleSwitchColors}
        onChangeBorderColor={handleChangeBorderColor}
        onChangeCenterColor={handleChangeCenterColor}
        onChangeBorderGradient={(params) =>
          handleChangeGradient({ ...params, parentDbField: 'corner_border', parentField: 'cornerBorder' })
        }
        onChangeCenterGradient={(params) =>
          handleChangeGradient({ ...params, parentDbField: 'corner_center', parentField: 'cornerCenter' })
        }
        onChangeIsBorderGradient={(e) =>
          handleChangeIsGradient({
            dbField: 'corner_border',
            field: 'cornerBorder',
            checked: e.target.checked,
            defaultValue: qrCode?.style?.cornerBorder?.color,
          })
        }
        onChangeIsCenterGradient={(e) =>
          handleChangeIsGradient({
            dbField: 'corner_center',
            field: 'cornerCenter',
            checked: e.target.checked,
            defaultValue: qrCode?.style?.cornerCenter?.color,
          })
        }
        qrCodeStyles={qrCode?.style}
      />
      <QRCodeCorrectionLevel
        levels={qrCodeStylesTemplates?.errorCorrectionLevels}
        defaultSelectedLevel={qrCode?.style?.errorCorrectionLevel}
        onSelectLevel={updateQrCodeCorrection}
      />
      <QRCodeLogo
        selectedLogo={selectedLogo}
        onSelectLogo={onAddImageHandler}
        onDeleteUploadedLogo={onRemoveImageHandler}
      />
    </Box>
  );
};
